import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";
import { SortableAdapter } from "../cms-builder/adapters/SortableAdapter";
import type { SortableMoveEvent } from "../cms-builder/adapters/SortableAdapter";

interface TreeItem { id: number; parent_id: number | null; sort_order: number; }

/**
 * CMS 메뉴 항목 트리 드래그앤드롭(부모/자식 중첩). admin-page-tree 마크업 재사용.
 * PATCH(=POST + X-HTTP-Method-Override) reorderUrl  body { items:[{id,parent_id,sort_order}] }.
 */
class CmsMenuTree {
    readonly #root: HTMLElement;
    readonly #endpoint: string;
    readonly #csrf: string;
    readonly #instances: SortableAdapter[] = [];
    #dropTarget: HTMLElement | null = null;
    static readonly #GROUP = "cms-menu-tree";
    static readonly #MAX_DEPTH = 3;

    constructor(root: HTMLElement) {
        this.#root = root;
        this.#endpoint = root.dataset.reorderUrl ?? "";
        this.#csrf =
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
            document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
    }

    init(): void {
        if (!this.#endpoint) return;
        this.#root.querySelectorAll<HTMLElement>("[data-page-tree-root], [data-page-children]").forEach((ul) => {
            const sortable = new SortableAdapter(ul);
            sortable.mount({
                group: CmsMenuTree.#GROUP,
                animation: 150,
                fallbackOnBody: true,
                swapThreshold: 0.6,
                handle: "[data-page-drag-handle]",
                draggable: ".admin-page-tree__item",
                ghostClass: "admin-page-tree__ghost",
                dragClass: "admin-page-tree__drag",
                chosenClass: "admin-page-tree__chosen",
                fallbackClass: "admin-page-tree__fallback",
                onChoose: () => this.#root.classList.add("is-drag-ready"),
                onStart: () => this.#root.classList.add("is-dragging"),
                onMove: (evt) => this.#allowMove(evt),
                onEnd: () => {
                    this.#clearDragState();
                    void this.#save();
                },
                onUnchoose: () => this.#clearDragState(),
            });
            this.#instances.push(sortable);
        });
    }

    #allowMove(evt: SortableMoveEvent): boolean {
        let depth = 1;
        let el: HTMLElement | null = evt.to.parentElement;
        while (el && el !== this.#root) {
            if (el.classList && el.classList.contains("admin-page-tree__item")) depth++;
            el = el.parentElement;
        }
        const allowed = depth <= CmsMenuTree.#MAX_DEPTH;
        this.#setDropTarget(allowed ? evt.to : null);
        this.#root.classList.toggle("is-drop-denied", !allowed);
        return allowed;
    }

    #setDropTarget(target: HTMLElement | null): void {
        if (this.#dropTarget === target) return;
        this.#dropTarget?.classList.remove("is-drop-target");
        this.#dropTarget = target;
        this.#dropTarget?.classList.add("is-drop-target");
    }

    #clearDragState(): void {
        this.#setDropTarget(null);
        this.#root.classList.remove("is-drag-ready", "is-dragging", "is-drop-denied");
    }

    #serialize(): TreeItem[] {
        const out: TreeItem[] = [];
        const walk = (ul: HTMLElement, parentId: number | null): void => {
            Array.from(ul.children)
                .filter((c) => (c as HTMLElement).classList.contains("admin-page-tree__item"))
                .forEach((li, i) => {
                    const id = parseInt((li as HTMLElement).dataset.id ?? "0", 10);
                    if (!id) return;
                    out.push({ id, parent_id: parentId, sort_order: (i + 1) * 10 });
                    const child = (li as HTMLElement).querySelector<HTMLElement>(":scope > [data-page-children]");
                    if (child) walk(child, id);
                });
        };
        const root = this.#root.querySelector<HTMLElement>("[data-page-tree-root]");
        if (root) walk(root, null);
        return out;
    }

    async #save(): Promise<void> {
        const items = this.#serialize();
        if (items.length === 0) return;
        document.documentElement.classList.add("admin--loading");
        try {
            const res = await fetch(this.#endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-HTTP-Method-Override": "PATCH",
                    "X-CSRF-TOKEN": this.#csrf,
                },
                body: JSON.stringify({ items, _token: this.#csrf }),
            });
            const json = (await res.json().catch(() => ({}))) as { result?: string; msg?: string };
            if (res.ok && json.result === "success") {
                Toast.success("순서가 저장되었습니다.");
            } else {
                await Modal.alert(json.msg ?? "순서 저장 실패. 새로고침 후 다시 시도해주세요.");
                window.location.reload();
            }
        } catch {
            await Modal.alert("순서 저장 중 오류가 발생했습니다.");
            window.location.reload();
        } finally {
            document.documentElement.classList.remove("admin--loading");
        }
    }
}

interface TreeRoot extends HTMLElement { _cmsMenuTreeInited?: boolean; }

export function initCmsMenuTree(): void {
    document.querySelectorAll<TreeRoot>("[data-cms-menu-tree]").forEach((el) => {
        if (el._cmsMenuTreeInited) return;
        el._cmsMenuTreeInited = true;
        new CmsMenuTree(el).init();
    });
}
